// src/services/authService.ts
import { getAuth, signInWithCustomToken, UserCredential } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { config } from '../config';
import { VerifyCodeResult, AuthError } from '../types/auth';

export const authService = {
  // X OAuth2認証（リダイレクト版）
  signInWithX: async (): Promise<void> => {
    try {
      const generateAuthUrl = httpsCallable(functions, 'generateXAuthUrl');
      const useLocalhost = window.location.hostname === 'localhost';
      const result = await generateAuthUrl({ mode: 'login', useLocalhost, app: 'orifoli' });
      const data = result.data as { authUrl: string };

      if (data.authUrl) {
        // リダイレクトしてX認証ページへ
        window.location.href = data.authUrl;
      } else {
        throw new Error('認証URLの生成に失敗しました');
      }
    } catch (error: any) {
      console.error('X OAuth2認証エラー:', error);
      throw error;
    }
  },

  // カスタムトークンでサインイン（コールバックから呼ばれる）
  signInWithCustomToken: async (token: string): Promise<UserCredential> => {
    const auth = getAuth();
    return signInWithCustomToken(auth, token);
  },

  // 認証コードをメールで送信する
  sendVerificationCode: async (email: string, language: string = 'ja') => {
    const sendVerificationCodeUrl = config.functionUrls.sendVerificationCode;

    try {
      // console.log(`認証コードを送信しています: ${email}, 言語: ${language}`);

      const response = await fetch(sendVerificationCodeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          language: language
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data = await response.json();
      // console.log("認証コード送信成功:", data);

      return data;

    } catch (error) {
      console.error("sendVerificationCode Cloud Function error:", error);
      let errorCode = 'unknown';
      let errorMessage = 'An unexpected error occurred.';

      if (error instanceof Error) {
        errorMessage = error.message;
      }

      throw { code: errorCode, message: errorMessage } as AuthError;
    }
  },

  // 認証コードを検証してログインする
  verifyCode: async (email: string, code: string): Promise<VerifyCodeResult> => {
    const verifyCodeUrl = config.functionUrls.verifyCode;

    try {
      // console.log(`認証コードを検証しています: ${email}, コード: ${code}`);

      const response = await fetch(verifyCodeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email, code: code }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      const data = await response.json() as VerifyCodeResult;
      // console.log("認証コード検証結果:", data);

      if (data.token) {
        // console.log("トークンの形式をチェック:", {
        //   token: data.token.substring(0, 20) + "..."
        // });

        if (typeof data.token !== 'string' || data.token.length < 20) {
          console.error("トークンの形式が不正です");
          throw new Error("無効なトークン形式です");
        }

        try {
          const auth = getAuth();
          await signInWithCustomToken(auth, data.token);
          // console.log("カスタムトークンでのサインイン成功");
        } catch (signInError) {
          console.error("サインインエラー:", signInError);
          throw signInError;
        }
      }

      return data;

    } catch (error) {
      console.error("verifyCode Cloud Function error:", error);
      let errorCode = 'unknown';
      let errorMessage = 'An unexpected error occurred.';

      if (error instanceof Error) {
        errorMessage = error.message;
      }
      throw { code: errorCode, message: errorMessage } as AuthError;
    }
  },
};
